import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/feature/SEOHead';
import CategoryCard from '@/components/feature/CategoryCard';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';
import { siteInfo } from '@/mocks/siteInfo';
import { useState } from 'react';

const featuredArticles = articles.slice(0, 6);

const heroStats = [
  { label: '文章教程', value: '50+' },
  { label: '内容分类', value: '6' },
  { label: '月访问量', value: '12万+' },
  { label: '更新频率', value: '每日' },
];

const schemaOrg = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteInfo.name,
  url: 'https://caoliu-guide.com',
  description: siteInfo.description,
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://caoliu-guide.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const schemaWebPage = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: siteInfo.title,
  description: siteInfo.description,
  url: 'https://caoliu-guide.com',
  isPartOf: {
    '@type': 'WebSite',
    name: siteInfo.name,
    url: 'https://caoliu-guide.com',
  },
};

const schemaFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <Layout>
      <SEOHead
        title={siteInfo.title}
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl="https://caoliu-guide.com"
        ogImage="https://readdy.ai/api/search-image?query=Modern%20website%20navigation%20guide%20portal%20with%20compass%20icon%2C%20abstract%20digital%20map%2C%20soft%20warm%20gradient%20background%2C%20minimalist%20design%2C%20professional%20editorial%20style&width=1200&height=630&seq=seo-home-og&orientation=landscape"
        schemaJsonLd={[schemaOrg, schemaWebPage, schemaFAQ]}
      />

      <section className="relative w-full min-h-[580px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20modern%20digital%20landscape%20with%20soft%20warm%20gradients%2C%20geometric%20connection%20nodes%20and%20lines%2C%20subtle%20compass%20navigation%20elements%2C%20minimalist%20tech%20background%2C%20clean%20atmospheric%20composition%2C%20warm%20neutral%20tones%2C%20professional%20web%20design%20background%2C%20no%20text&width=1600&height=900&seq=hero-bg-home&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
            width={1600}
            height={900}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/20 mb-8">
            <div className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></div>
            <span className="text-sm text-background-50/90 whitespace-nowrap">
              2026年8月 · 最新官方入口已更新
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-5 leading-tight">
            {siteInfo.title}
          </h1>
          <p className="text-base md:text-lg text-background-50/80 mb-10 max-w-2xl mx-auto leading-relaxed">
            {siteInfo.description}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-lg mx-auto">
            <div className="relative w-full">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索 caoliu 相关教程和指南..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-background-50 text-foreground-950 placeholder:text-foreground-400 text-sm border border-background-200/60 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all"
              />
            </div>
            <Link
              to={`/category/usage-guide`}
              className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              开始探索
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12 max-w-2xl mx-auto">
            {heroStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50">
                  {stat.value}
                </div>
                <div className="text-xs text-background-50/60 mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              内容分类导航
            </h2>
            <p className="text-foreground-600 max-w-xl mx-auto">
              按照主题分类浏览 caoliu 相关教程，快速找到您需要的内容
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <CategoryCard key={cat.slug} category={cat} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                精选文章
              </h2>
              <p className="text-foreground-600">
                最受用户欢迎的高质量 caoliu 使用指南和教程
              </p>
            </div>
            <Link
              to="/category/usage-guide"
              className="text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap flex items-center gap-1"
            >
              查看全部文章
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              常见问题
            </h2>
            <p className="text-foreground-600">
              关于 caoliu 的常见疑问，在这里都能找到答案
            </p>
          </div>

          <div className="space-y-3">
            {faqItems.map((faq) => (
              <details
                key={faq.id}
                className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-100/50 transition-colors">
                  <h4 className="text-sm font-medium text-foreground-950 pr-4">
                    {faq.question}
                  </h4>
                  <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                </summary>
                <div className="px-6 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/category/faq-help"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors"
            >
              查看更多常见问题
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-14 px-4 md:px-6 bg-primary-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-4">
            获取 caoliu 最新动态
          </h2>
          <p className="text-background-50/80 mb-8">
            关注我们的官方频道，第一时间获取 caoliu 最新入口地址和使用指南更新
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={siteInfo.socialLinks.telegram}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-background-50 text-foreground-950 text-sm font-semibold hover:bg-background-50/90 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-telegram-line"></i>
              Telegram 频道
            </a>
            <a
              href={siteInfo.socialLinks.twitter}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-background-50/15 text-background-50 text-sm font-semibold hover:bg-background-50/25 border border-background-50/30 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-twitter-x-line"></i>
              关注 Twitter
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}